import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import {
    FiPieChart,
    FiUsers,
    FiRepeat,
    FiCalendar,
    FiUser,
    FiLogOut,
    FiRefreshCw,
    FiLayers,
    FiActivity,
    FiMessageSquare
} from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';
import NotificationBell from '../notifications/NotificationBell';
import { getCoordinatorById } from '../coordinators/coordinatorService';
import { getAllGuilds } from '../guilds/guildService';
import DoctorList from '../doctors/DoctorList';
import { getDoctorsByMultipleGuilds } from '../doctors/doctorService';
import ReferralList from '../referrals/ReferralList';
import { getReferralsByMultipleGuilds } from '../referrals/referralService';
import CoordinatorEventList from '../events/CoordinatorEventList';
import CoordinatorTestimonialList from '../testimonials/CoordinatorTestimonialList';
import IncomingAppointments from '../appointments/IncomingAppointments';
import './Dashboard.css';

export default function CoordinatorDashboard() {
    const { currentUser, signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [coordinator, setCoordinator] = useState(null);
    const [guilds, setGuilds] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadCoordinatorData = React.useCallback(async () => {
        if (!currentUser) return;
        try {
            setLoading(true);
            const [coordData, guildsData] = await Promise.all([
                getCoordinatorById(currentUser.uid),
                getAllGuilds()
            ]);
            setCoordinator(coordData);
            setGuilds(guildsData.filter(g => coordData?.assignedGuilds?.includes(g.id)));
        } catch (error) {
            console.error('Error loading coordinator dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        loadCoordinatorData();
    }, [loadCoordinatorData]);

    async function handleSignOut() {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    }

    const isActive = (path) => location.pathname === path;

    if (loading) {
        return (
            <div className="loading-spinner">
                <FiRefreshCw className="spin-icon" />
                <p>Loading Dashboard...</p>
            </div>
        );
    }

    return (
        <div className="dashboard-layout coordinator-theme">
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <h2>AssistHealth</h2>
                    <p>Guild Lite Coordinator</p>
                </div>

                <nav className="sidebar-nav">
                    <Link to="/dashboard" className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiPieChart /></span>
                        <span className="nav-text">Insights</span>
                    </Link>
                    <Link to="/dashboard/doctors" className={`nav-item ${location.pathname.startsWith('/dashboard/doctors') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiUsers /></span>
                        <span className="nav-text">Guild Doctors</span>
                    </Link>

                    <Link to="/dashboard/referrals" className={`nav-item ${location.pathname.startsWith('/dashboard/referrals') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiRepeat /></span>
                        <span className="nav-text">Referral Desk</span>
                    </Link>
                    <Link to="/dashboard/appointments" className={`nav-item ${location.pathname.startsWith('/dashboard/appointments') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiCalendar /></span>
                        <span className="nav-text">Appointments</span>
                    </Link>
                    <Link to="/dashboard/events" className={`nav-item ${location.pathname.startsWith('/dashboard/events') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiCalendar /></span>
                        <span className="nav-text">Events</span>
                    </Link>
                    <Link to="/dashboard/testimonials" className={`nav-item ${location.pathname.startsWith('/dashboard/testimonials') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiMessageSquare /></span>
                        <span className="nav-text">Testimonials</span>
                    </Link>
                    <Link to="/dashboard/profile" className={`nav-item ${isActive('/dashboard/profile') ? 'active' : ''}`}>
                        <span className="nav-icon"><FiUser /></span>
                        <span className="nav-text">Profile</span>
                    </Link>
                </nav>

                <div className="sidebar-footer">
                    <button onClick={handleSignOut} className="signout-button">
                        <span className="nav-icon"><FiLogOut /></span>
                        <span className="nav-text">Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <main className="main-content">
                <header className="main-header">
                    <div className="header-left">
                    </div>
                    <div className="header-right">
                        <NotificationBell userId={currentUser?.uid} role="coordinator" />
                    </div>
                </header>

                <Routes>
                    <Route path="/" element={<CoordinatorOverview coordinator={coordinator} guilds={guilds} />} />
                    <Route path="/doctors" element={<DoctorList restrictedGuilds={coordinator?.assignedGuilds} readOnly={true} />} />
                    <Route path="/referrals" element={<ReferralList restrictedGuilds={coordinator?.assignedGuilds} />} />
                    <Route path="/events" element={<CoordinatorEventList />} />
                    <Route path="/testimonials" element={<CoordinatorTestimonialList />} />
                    <Route path="/appointments" element={<IncomingAppointments />} />
                    <Route path="/profile" element={<CoordinatorProfile coordinator={coordinator} guilds={guilds} onLogout={handleSignOut} />} />
                </Routes>
            </main>
        </div>
    );
}

function CoordinatorOverview({ coordinator, guilds }) {
    const [stats, setStats] = useState({ pendingReferrals: 0, doctorCounts: {} });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchStats() {
            try {
                if (!coordinator?.assignedGuilds || coordinator.assignedGuilds.length === 0) {
                    setStats({ pendingReferrals: 0, doctorCounts: {} });
                    setLoading(false);
                    return;
                }

                const assignedGuilds = coordinator.assignedGuilds;

                const [referrals, doctors] = await Promise.all([
                    getReferralsByMultipleGuilds(assignedGuilds),
                    getDoctorsByMultipleGuilds(assignedGuilds)
                ]);

                // Filter referrals for pending status
                const pending = referrals.filter(r => r.status === 'pending').length;

                // Calculate doctor counts per guild
                const counts = {};
                assignedGuilds.forEach(guildId => {
                    counts[guildId] = doctors.filter(d => d.guildId === guildId).length;
                });

                setStats({ pendingReferrals: pending, doctorCounts: counts });
            } catch (error) {
                console.error('Error fetching coordinator stats:', error);
            } finally {
                setLoading(false);
            }
        }
        if (coordinator) fetchStats();
    }, [coordinator]);

    return (
        <div className="page-container">
            <div className="welcome-banner">
                <h1>Welcome, {coordinator?.fullName}</h1>
                <p className="subtitle">Managing {guilds.length} medical guilds in the network</p>
            </div>

            <div className={`stats-grid ${loading ? 'stats-loading' : ''}`}>
                {/* Individual Guild Cards */}
                {guilds.map(g => (
                    <div key={g.id} className="stat-card guild-card">
                        <div className="card-header">
                            <FiLayers className="card-icon" />
                            <h3>{g.name}</h3>
                        </div>
                        <div className="card-content">
                            <p className="stat-value">{stats.doctorCounts[g.id] || 0}</p>
                            <span className="stat-label">Doctors in Guild</span>
                        </div>
                    </div>
                ))}

                {guilds.length === 0 && (
                    <div className="stat-card empty-card">
                        <div className="card-header">
                            <FiLayers className="card-icon" />
                            <h3>No Guilds Assigned</h3>
                        </div>
                        <p className="empty-msg">Contact admin to assign guilds.</p>
                    </div>
                )}

                {/* Pending Referrals Card */}
                <div className="stat-card referral-card">
                    <div className="card-header">
                        <FiActivity className="card-icon" />
                        <h3>Pending Referrals</h3>
                    </div>
                    <div className="card-content">
                        <p className="stat-value">{stats.pendingReferrals}</p>
                        <span className="stat-label">Awaiting Action</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CoordinatorProfile({ coordinator, guilds, onLogout }) {
    return (
        <div className="page-container profile-page">
            <h1>My Profile</h1>
            <div className="profile-card">
                <div className="profile-info-group">
                    <label>Full Name</label>
                    <p>{coordinator?.fullName || 'N/A'}</p>
                </div>
                <div className="profile-info-group">
                    <label>Email Address</label>
                    <p>{coordinator?.email}</p>
                </div>
                <div className="profile-info-group">
                    <label>Assigned Guilds</label>
                    <div className="guild-tags">
                        {guilds.map(g => (
                            <span key={g.id} className="guild-tag">{g.name}</span>
                        ))}
                    </div>
                </div>
                <button onClick={onLogout} className="btn-secondary signout-profile-btn">
                    <FiLogOut />
                    <span>Logout</span>
                </button>
            </div>
        </div>
    );
}
